import { apiDelete, apiFetch, API_BASE_URL } from "@/lib/api-client";
import { client, user } from "@duress/shared";

export async function listClients(): Promise<client.ClientListItem[]> {
  return apiFetch("/clients", client.ClientListResponseSchema);
}

export async function getClientBySlug(slug: string): Promise<client.ClientListItem> {
  return apiFetch(`/clients/by-slug/${encodeURIComponent(slug)}`, client.ClientListItemSchema);
}

export async function createClient({
  name,
  industries,
  region,
}: {
  name: string;
  industries: string[];
  region: client.Region;
}): Promise<client.ClientListItem> {
  return apiFetch("/clients", client.ClientListItemSchema, {
    method: "POST",
    body: JSON.stringify({ name, industries, region }),
  });
}

export async function updateClientIndustries(
  id: string,
  industries: string[],
): Promise<client.ClientListItem> {
  return apiFetch(`/clients/${encodeURIComponent(id)}`, client.ClientListItemSchema, {
    method: "PATCH",
    body: JSON.stringify({ industries }),
  });
}

export async function deleteClient(id: string): Promise<void> {
  await apiDelete(`/clients/${encodeURIComponent(id)}`);
}

export async function getTierThresholds(
  clientId: string,
): Promise<client.TierThresholds> {
  return apiFetch(
    `/clients/${encodeURIComponent(clientId)}/tier-config`,
    client.TierThresholdsSchema,
  );
}

export async function getClientSnapshot(
  slug: string,
  month: string | null,
): Promise<client.ClientSnapshot> {
  const qs = month ? `?month=${encodeURIComponent(month)}` : "";
  return apiFetch(
    `/clients/by-slug/${encodeURIComponent(slug)}/snapshot${qs}`,
    client.ClientSnapshotSchema,
  );
}

export async function updateClientInfo(
  id: string,
  info: client.UpdateClientInfoRequest,
): Promise<client.ClientListItem> {
  return apiFetch(`/clients/${encodeURIComponent(id)}/info`, client.ClientListItemSchema, {
    method: "PATCH",
    body: JSON.stringify(info),
  });
}

export async function putTierThresholds(
  clientId: string,
  thresholds: client.TierThresholds,
): Promise<client.TierThresholds> {
  return apiFetch(
    `/clients/${encodeURIComponent(clientId)}/tier-config`,
    client.TierThresholdsSchema,
    { method: "PUT", body: JSON.stringify(thresholds) },
  );
}

export async function recordClientView(clientId: string): Promise<void> {
  // Fire-and-forget — errors here should never interrupt navigation.
  await fetch(`${API_BASE_URL}/v1/clients/${encodeURIComponent(clientId)}/view`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

export async function listRecentlyViewedClients(): Promise<client.ClientListItem[]> {
  return apiFetch("/clients/recently-viewed", client.ClientListResponseSchema);
}

export interface ExportScope {
  periodId?: string;
  from?: string;
  to?: string;
}

export async function downloadClientWorkbook(
  slug: string,
  scope: ExportScope,
): Promise<void> {
  const params = new URLSearchParams();
  if (scope.periodId) params.set("period", scope.periodId);
  if (scope.from) params.set("from", scope.from);
  if (scope.to) params.set("to", scope.to);
  const qs = params.toString();
  const url = `${API_BASE_URL}/v1/clients/by-slug/${encodeURIComponent(slug)}/export.xlsx${qs ? `?${qs}` : ""}`;
  await downloadWorkbook(url, "duress-export.xlsx");
}

export async function downloadDeviceList(slug: string): Promise<void> {
  const url = `${API_BASE_URL}/v1/clients/by-slug/${encodeURIComponent(slug)}/device-list.xlsx`;
  await downloadWorkbook(url, "duress-device-list.xlsx");
}

async function downloadWorkbook(url: string, fallbackFilename: string): Promise<void> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Export failed (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    parseFilenameFromContentDisposition(res.headers.get("Content-Disposition")) ??
    fallbackFilename;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function listClientContacts(
  clientId: string,
): Promise<client.ClientContact[]> {
  const res = await apiFetch(
    `/clients/${encodeURIComponent(clientId)}/contacts`,
    client.ClientContactListResponseSchema,
  );
  return res.items;
}

export async function createClientContact(
  clientId: string,
  data: client.CreateClientContactRequest,
): Promise<client.ClientContact> {
  return apiFetch(
    `/clients/${encodeURIComponent(clientId)}/contacts`,
    client.ClientContactSchema,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export async function updateClientContact(
  clientId: string,
  contactId: string,
  data: client.UpdateClientContactRequest,
): Promise<client.ClientContact> {
  return apiFetch(
    `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
    client.ClientContactSchema,
    { method: "PUT", body: JSON.stringify(data) },
  );
}

export async function deleteClientContact(
  clientId: string,
  contactId: string,
): Promise<void> {
  await apiDelete(
    `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
  );
}

export async function updateClientCsm(
  id: string,
  userId: string | null,
): Promise<client.ClientListItem> {
  return apiFetch(`/clients/${encodeURIComponent(id)}/csm`, client.ClientListItemSchema, {
    method: "PATCH",
    body: JSON.stringify({ userId }),
  });
}

export async function listUsers(): Promise<user.UserListResponse> {
  return apiFetch("/users", user.UserListResponseSchema);
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? null;
}
